import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AccountsSortingService } from '../../../features/accounts/services/accounts-sorting.service';
import { AccountHelper } from '../../../domain/account.domain';
import { LtiAccountStatus } from '../../../domain/lti/lti.enum';
import { AccountsDescriptionService } from '../../../features/accounts/services/accounts-description.service';
import { LtiAccount } from '../../../domain/lti/lti.domain';
import { UtilityService } from '../../../utils/utility.service';
import { FundGoalRestService } from './fund-goal-rest.service';

export interface SelectableLtiAccount {
  selected: boolean;
  account: LtiAccount;
}

export interface FundGoalGroup {
  groupNumber: string;
  groupName: string;
  fxRate: number;
  fxRateAsOf: Date;
  accounts: Array<SelectableLtiAccount>;
  margin: Array<LtiAccount>;
  ineligible: Array<LtiAccount>;
  other: Array<LtiAccount>;
  ineligibleExpanded: boolean;
  otherExpanded: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class FundGoalService {
  private readonly fundGoalRestService = inject(FundGoalRestService);
  private readonly accountsDescriptionService = inject(AccountsDescriptionService);
  private readonly accountsSortingService = inject(AccountsSortingService);

  /**
   * get accounts
   *
   * @return
   */
  getAccounts(): Observable<Array<FundGoalGroup>> {
    return this.fundGoalRestService.getAccounts()
      .pipe(
        map((accounts: Array<LtiAccount>): Array<LtiAccount> => {
          const sortedAccounts = this.accountsSortingService.sort(accounts);
          return this.accountsDescriptionService.setDescriptions(sortedAccounts);
        })
      )
      .pipe(
        map((accounts: Array<LtiAccount>): Array<FundGoalGroup> => this.mapFundGoalGroup(accounts))
      );
  }

  /**
   * Map account to FundGoalGroup
   * group accounts into eligible, ineligible and other
   *
   * @param accounts
   * @return
   */
  private mapFundGoalGroup(accounts: Array<LtiAccount>): Array<FundGoalGroup> {
    const fundGoalGroups: Array<FundGoalGroup> = [];
    accounts.forEach((ltiAccount: LtiAccount): void => {
      let group: FundGoalGroup = fundGoalGroups.find((x: FundGoalGroup): boolean => x.groupNumber === ltiAccount.groupNumber);
      if (group === undefined) {
        group = this.createFundGoalGroup(ltiAccount);
        fundGoalGroups.push(group);
      }
      if (AccountHelper.isMarginAccount(ltiAccount)) {
        group.margin.push(ltiAccount);
      } else {
        this.addAccountToGroups(group, ltiAccount);
      }

      if (group.fxRate === undefined && ltiAccount.fxRate !== undefined) {
        group.fxRate = ltiAccount.fxRate.rate;
        group.fxRateAsOf = ltiAccount.balanceDate;
      }
    });
    this.updateMarginDisplayAccount(fundGoalGroups);
    return fundGoalGroups;
  }

  private createFundGoalGroup(ltiAccount: LtiAccount): FundGoalGroup {
    return {
      groupNumber: ltiAccount.groupNumber,
      groupName: ltiAccount.businessLineDescription,
      fxRate: undefined,
      fxRateAsOf: undefined,
      accounts: [],
      margin: [],
      ineligible: [],
      other: [],
      ineligibleExpanded: false,
      otherExpanded: false
    };
  }

  /**
   * Separate accounts base on status
   *
   * @param group
   * @param ltiAccount
   */
  private addAccountToGroups(group: FundGoalGroup, ltiAccount: LtiAccount): void {
    if (ltiAccount.ltiStatus === LtiAccountStatus.LTI_ELIGIBLE ||  ltiAccount.ltiStatus === LtiAccountStatus.LTI_OCCUPIED) {
      this.addAccountToGroup(AccountHelper.isMarginAccount(ltiAccount), this.createSelectableLtiAccount(ltiAccount), group.accounts);
    } else if (ltiAccount.ltiStatus ===   LtiAccountStatus.LTI_INELIGIBLE) {
      this.addAccountToGroup(AccountHelper.isMarginAccount(ltiAccount), ltiAccount, group.ineligible);
    } else if (ltiAccount.ltiStatus ===   LtiAccountStatus.LTI_UNAVAILABLE) {
      this.addAccountToGroup(AccountHelper.isMarginAccount(ltiAccount), ltiAccount, group.other);
    }
  }

  private addAccountToGroup<T>(isMargin: boolean, account: T, group: Array<T>): void {
    if (isMargin) {
      group.unshift(account);
    } else {
      group.push(account);
    }
  }

  /**
   * Update display margin account
   * Sum total balance for all margin accounts
   *
   * @param goalGroup
   */
  private updateMarginDisplayAccount(goalGroup: Array<FundGoalGroup>): void {
    goalGroup.forEach((group: FundGoalGroup): void => {
      const accounts: Array<LtiAccount> = group.margin;
      if (accounts.length !== 0) {
        const displayAccount = UtilityService.deepClone(accounts[0]);
        accounts.forEach((ltiAccount: LtiAccount, index: number): void => {
          if (index > 0) {

            displayAccount.number = `${displayAccount.number}/${ltiAccount.type.stringValue}`;
            displayAccount.totalBalance += ltiAccount.totalBalance;
            if (displayAccount.currency !== ltiAccount.currency) {
              displayAccount.currency = undefined;
            }
          }
        });
        this.addAccountToGroups(group, displayAccount);
      }
    });
  }

  private createSelectableLtiAccount(ltiAccount: LtiAccount): SelectableLtiAccount {
    return { selected: false, account: ltiAccount };
  }
}

